import React, { useEffect, useState, useRef } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/contexts/LoaderContext";
import { Clock, CheckCircle2, Loader2, FileText, AlertCircle } from "lucide-react";
import JourneyHeader from "@/components/JourneyHeader";

const MIN_WAIT_SECONDS = 10; // Minimum wait time before allowing submission

const DocPushJourney = ({ isBuilderMode = false }: { isBuilderMode?: boolean }) => {
    const navigate = useNavigate();
    const { setLoading, setLoaderMessage } = useLoader();
    // @origin: v2 | @since: 5fe70ff4 | @used-by: ALL | @purpose: dedicated
    // "premature" UI state -- distinct from 'failed' -- for the case where a
    // mandatory PrematurePushLog pre-check blocked the push synchronously
    // (e.g. NACH mandate not yet activated, bank details incomplete). Shows a
    // friendlier "team will compile documents" message with no retry button,
    // since retrying immediately would just hit the same guard again.
    const [status, setStatus] = useState<'checking' | 'ready' | 'pushing' | 'completed' | 'failed' | 'premature'>('checking');
    const [error, setError] = useState<string | null>(null);
    const hasTriggered = useRef(false);
    const [documentReady, setDocumentReady] = useState(false);
    const [minTimerElapsed, setMinTimerElapsed] = useState(false);
    const [countdown, setCountdown] = useState(MIN_WAIT_SECONDS);

    // Minimum timer: prevent immediate submission after page load
    useEffect(() => {
        const timer = setTimeout(() => {
            setMinTimerElapsed(true);
        }, MIN_WAIT_SECONDS * 1000);

        // Countdown display
        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearTimeout(timer);
            clearInterval(interval);
        };
    }, []);

    // Poll for signed document readiness
    useEffect(() => {
        if (documentReady) return; // Already ready, stop polling

        let cancelled = false;
        const poll = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/check_signed_document_ready/`);
                if (!cancelled && res.data?.ready) {
                    setDocumentReady(true);
                    console.log("Signed document is ready for push");
                }
            } catch (err) {
                console.error("Error checking signed document readiness:", err);
            }
        };

        // Initial check
        poll();
        // Poll every 5 seconds
        const interval = setInterval(poll, 5000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [documentReady]);

    // When both conditions are met, transition to 'ready' status
    useEffect(() => {
        if (documentReady && minTimerElapsed && status === 'checking') {
            setStatus('ready');
        }
    }, [documentReady, minTimerElapsed, status]);

    const pushPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Stop polling on unmount
    useEffect(() => {
        return () => {
            if (pushPollRef.current) clearInterval(pushPollRef.current);
        };
    }, []);

    const finishWithSuccess = (nextUrl: string) => {
        setStatus('completed');
        setLoaderMessage(null);
        setLoading(false);
        // Short delay so user can see "Successful" message
        setTimeout(() => {
            navigate(nextUrl || "/loan-success");
        }, 2000);
    };

    // Poll backend for async push_loan_documents_task completion (avoids
    // blocking the request, which previously caused a 504 after 30s)
    const pollDocPushStatus = () => {
        if (pushPollRef.current) clearInterval(pushPollRef.current);
        pushPollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/check_doc_push_status/`);
                const data = res.data;

                if (data.status === 'DOCS_PUSHED') {
                    clearInterval(pushPollRef.current!);
                    finishWithSuccess(data.next_page_url);
                } else if (data.status === 'PUSH_FAILED') {
                    clearInterval(pushPollRef.current!);
                    setStatus('failed');
                    setError(data.message || "Something went wrong. Please try again.");
                    setLoaderMessage(null);
                    setLoading(false);
                } else if (data.status === 'DOC_PUSH_REJECTED') {
                    clearInterval(pushPollRef.current!);
                    setLoaderMessage(null);
                    setLoading(false);
                    navigate('/loan-offers');
                }
                // PUSHING / READY_TO_PUSH -> keep polling
            } catch (err) {
                console.error("Error polling doc push status:", err);
            }
        }, 3000);
    };

    // @origin: v2 | @since: 0410b2e7..5fe70ff4 window | @used-by: ALL |
    // @purpose: push_loan_documents/ is now ASYNC -- it returns 202
    // {success:true, queued:true} immediately after enqueueing the Celery
    // task, rather than the final push result. This handler now starts
    // pollDocPushStatus() (previously dead code, defined above but never
    // called) to await the actual outcome instead of assuming immediate
    // completion. "already_pushed" (idempotent re-entry) still completes
    // immediately since no task was queued for it.
    const handlePushDocuments = async () => {
        if (status === 'pushing') return;

        setError(null);
        setStatus('pushing');
        setLoading(true);
        setLoaderMessage("doc-push");

        try {
            const response = await axios.post(`${API_BASE_URL}/api/push_loan_documents/`);
            const data = response.data;

            if (data.already_pushed) {
                // Idempotent re-entry: documents were already pushed by a
                // prior call, no task was queued -- finish immediately.
                finishWithSuccess(data.next_page_url);
            } else if (data.queued) {
                // Async task enqueued -- poll check_doc_push_status/ for
                // the real outcome (DOCS_PUSHED / PUSH_FAILED / DOC_PUSH_REJECTED).
                pollDocPushStatus();
            } else if (data.is_premature) {
                // @origin: v2 | @since: 5fe70ff4 | @used-by: ALL | @purpose:
                // A PrematurePushLog guard blocked the push synchronously
                // (e.g. NACH mandate not yet activated, bank details
                // incomplete). v2 shows a dedicated "Application Received"
                // state here instead of the generic 'failed' state -- retrying
                // immediately would just hit the same guard again, so no
                // retry button is shown; the backend message is still
                // surfaced underneath for support/debugging visibility.
                setStatus('premature');
                setError(data.message || null);
                setLoaderMessage(null);
                setLoading(false);
            } else if (data.success) {
                // Defensive fallback for any legacy/synchronous success
                // shape that might still slip through.
                finishWithSuccess(data.next_page_url);
            } else {
                throw new Error(data.message || "Failed to push documents");
            }
        } catch (err: any) {
            console.error("Doc push error:", err);
            setStatus('failed');
            setError(err.response?.data?.message || err.message || "Something went wrong. Please try again.");
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    useEffect(() => {
        // No automatic trigger as per user request
    }, []);

    if (isBuilderMode) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
                <div className="text-2xl mb-1">📤</div>
                <p className="font-semibold text-gray-500">Document Push</p>
                <p className="text-xs mt-1">Submits all verified documents to the lender and advances journey on success.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-intellend-white">
            <JourneyHeader />

            <main className="container mx-auto px-4 py-12 max-w-2xl text-center">
                <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                    {status === 'checking' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <Loader2 className="h-12 w-12 text-intellend-green animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Preparing Your Documents</h2>
                            <p className="text-gray-600">
                                We're retrieving your signed agreement from the e-sign provider.
                                This usually takes a few seconds...
                            </p>
                            {!documentReady && (
                                <p className="text-sm text-gray-400">
                                    Waiting for signed document from Digio...
                                </p>
                            )}
                            {documentReady && !minTimerElapsed && (
                                <p className="text-sm text-gray-400">
                                    Almost ready... ({countdown}s)
                                </p>
                            )}
                        </div>
                    )}

                    {status === 'ready' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <FileText className="h-12 w-12 text-intellend-green" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Ready to Submit</h2>
                            <p className="text-gray-600">
                                Your signed agreement is ready. Click below to push your verified documents to the lender for final processing.
                            </p>
                            <button
                                onClick={handlePushDocuments}
                                className="w-full bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold py-4 rounded-lg transition-all"
                            >
                                Submit Application
                            </button>
                        </div>
                    )}

                    {status === 'pushing' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <Loader2 className="h-12 w-12 text-intellend-green animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Finalizing Your Application</h2>
                            <p className="text-gray-600">
                                We are pushing your verified documents to the lender for final processing.
                                Please do not close this window.
                            </p>
                        </div>
                    )}

                    {status === 'completed' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="p-3 bg-green-100 rounded-full">
                                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Documents Submitted</h2>
                            <p className="text-gray-600">
                                Your documents have been successfully pushed. Moving to the final step...
                            </p>
                        </div>
                    )}

                    {status === 'premature' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="p-3 bg-blue-100 rounded-full">
                                    <CheckCircle2 className="h-12 w-12 text-blue-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Application Received</h2>
                            <p className="text-gray-600 text-base">
                                Our team will compile the necessary documents and send your application to the bank.
                            </p>
                            <p className="text-gray-500 text-sm">
                                We may reach out to you in case any documentation is needed.
                            </p>
                            {error && (
                                <p className="text-xs text-gray-400">{error}</p>
                            )}
                        </div>
                    )}

                    {status === 'failed' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="p-4 bg-amber-100 rounded-full">
                                    <Clock className="h-12 w-12 text-amber-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Application Processing</h2>
                            <p className="text-gray-500 text-base">
                                application documents processing is in progress
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                                This may take a few moments. You can check back shortly or retry below.
                            </div>
                            <button
                                onClick={() => {
                                    hasTriggered.current = false;
                                    handlePushDocuments();
                                }}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 rounded-lg transition-all"
                            >
                                Check Again
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default DocPushJourney;
